import { useState, useEffect, useMemo } from 'react';
import { 
  BarChart3, 
  Users, 
  Eye, 
  Scale, 
  Save, 
  Lightbulb,
  TrendingUp,
  TrendingDown,
  Clock,
  RefreshCw,
  Loader2,
  Calendar,
  ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { analyticsApi, platformsApi } from '@/services/api';
import { cn } from '@/lib/utils';

// Date range presets
const DATE_PRESETS = [
  { label: 'All Time', value: 'all', days: null },
  { label: 'Last 7 Days', value: '7d', days: 7 },
  { label: 'Last 14 Days', value: '14d', days: 14 },
  { label: 'Last 30 Days', value: '30d', days: 30 },
  { label: 'Last 90 Days', value: '90d', days: 90 },
];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const getDateRange = (days) => {
  if (!days) return { start: null, end: null };
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0]
  };
};

const StatCard = ({ title, value, icon: Icon, description, trend, trendValue }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className="text-3xl font-semibold text-foreground">{value}</p>
          {description && (
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          )}
        </div>
        <div className="p-2 bg-primary/10 rounded-lg">
          <Icon className="w-5 h-5 text-primary" />
        </div>
      </div>
      {trend && (
        <div className={cn(
          "flex items-center gap-1 mt-3 text-xs",
          trend === 'up' ? 'text-success' : trend === 'down' ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : 
           trend === 'down' ? <TrendingDown className="w-3 h-3" /> : null}
          {trendValue}
        </div>
      )}
    </CardContent>
  </Card>
);

// Simple bar chart component
const SimpleBarChart = ({ data, height = 200 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center text-muted-foreground text-sm" style={{ height }}>
        No data available for this period
      </div>
    );
  }

  const maxValue = Math.max(...data.map(d => d.total), 1);
  const chartHeight = height - 40; // Account for x-axis labels and padding

  return (
    <div className="relative overflow-hidden" style={{ height }}>
      {/* Y-axis labels */}
      <div className="absolute left-0 top-0 w-10 flex flex-col justify-between text-xs text-muted-foreground pr-2 text-right" style={{ height: chartHeight }}>
        <span>{maxValue}</span>
        <span>{Math.round(maxValue / 2)}</span>
        <span>0</span>
      </div>
      
      {/* Chart area with border */}
      <div className="ml-12 border-l border-b border-border relative" style={{ height: chartHeight }}>
        <div className="absolute inset-0 flex items-end gap-1 p-1">
          {data.map((item, index) => {
            const barHeightPercent = (item.total / maxValue) * 100;
            
            return (
              <div
                key={item.date}
                className="flex-1 flex flex-col items-center group relative min-w-[20px] max-w-[60px]"
                style={{ height: '100%' }}
              >
                {/* Tooltip */}
                <div className="absolute bottom-full mb-2 hidden group-hover:block z-10 bg-popover border rounded-md shadow-md p-2 text-xs whitespace-nowrap">
                  <p className="font-medium">{formatDate(item.date)}</p>
                  <p className="text-muted-foreground">Total: {item.total}</p>
                  <p className="text-blue-600">Wizard: {item.wizard_complete || 0}</p>
                  <p className="text-green-600">Views: {item.platform_view || 0}</p>
                  <p className="text-purple-600">Comparisons: {item.comparison_view || 0}</p>
                </div>
                
                {/* Bar container - aligned to bottom */}
                <div className="w-full h-full flex items-end justify-center">
                  {/* Stacked bar */}
                  <div 
                    className="w-3/4 flex flex-col-reverse rounded-t overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ height: `${barHeightPercent}%`, minHeight: item.total > 0 ? '4px' : '0' }}
                  >
                    {item.wizard_complete > 0 && (
                      <div 
                        className="w-full bg-blue-500"
                        style={{ height: `${(item.wizard_complete / item.total) * 100}%` }}
                      />
                    )}
                    {item.platform_view > 0 && (
                      <div 
                        className="w-full bg-green-500"
                        style={{ height: `${(item.platform_view / item.total) * 100}%` }}
                      />
                    )}
                    {item.comparison_view > 0 && (
                      <div 
                        className="w-full bg-purple-500"
                        style={{ height: `${(item.comparison_view / item.total) * 100}%` }}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* X-axis labels */}
      <div className="ml-12 flex gap-1 p-1">
        {data.map((item, index) => (
          (data.length <= 14 || index % Math.ceil(data.length / 7) === 0) && (
            <div key={item.date} className="flex-1 min-w-[20px] max-w-[60px]">
              <span className="text-xs text-muted-foreground block text-center truncate">
                {formatDate(item.date)}
              </span>
            </div>
          )
        ))}
      </div>
    </div>
  );
};

export const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [datePreset, setDatePreset] = useState('7d');

  const currentPreset = DATE_PRESETS.find(p => p.value === datePreset) || DATE_PRESETS[0];
  const dateRange = useMemo(() => getDateRange(currentPreset.days), [currentPreset]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const [analyticsData, platformsData] = await Promise.all([
        analyticsApi.getSummary(dateRange.start, dateRange.end),
        platformsApi.getAll()
      ]);
      setAnalytics(analyticsData);
      setPlatforms(platformsData);
    } catch (err) {
      setError('Failed to load analytics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [datePreset]);

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button variant="outline" onClick={fetchAnalytics}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Date Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Analytics Overview</h2>
          <p className="text-sm text-muted-foreground">Platform engagement metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" data-testid="date-range-dropdown">
                <Calendar className="w-4 h-4" />
                {currentPreset.label}
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Select Date Range</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {DATE_PRESETS.map(preset => (
                <DropdownMenuItem
                  key={preset.value}
                  onClick={() => handlePresetChange(preset.value)}
                  className={cn(datePreset === preset.value && 'bg-accent')}
                >
                  {preset.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" onClick={fetchAnalytics}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Date Range Indicator */}
      {dateRange.start && (
        <div className="text-sm text-muted-foreground">
          Showing data from <span className="font-medium">{formatDate(dateRange.start)}</span> to <span className="font-medium">{formatDate(dateRange.end)}</span>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Wizard Completions"
          value={analytics?.wizard_completions || 0}
          icon={Users}
          description="Users who finished the questionnaire"
        />
        <StatCard
          title="Platform Views"
          value={analytics?.platform_views || 0}
          icon={Eye}
          description="Detail page visits"
        />
        <StatCard
          title="Comparisons"
          value={analytics?.comparisons || 0}
          icon={Scale}
          description="Side-by-side comparisons"
        />
        <StatCard
          title="Saved Results"
          value={analytics?.saved_results || 0}
          icon={Save}
          description="Shareable links created"
        />
      </div>

      {/* Time Series Chart */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            Activity Over Time
          </CardTitle>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-1.5 text-xs">
              <div className="w-3 h-3 rounded-sm bg-blue-500" />
              <span className="text-muted-foreground">Wizard</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <div className="w-3 h-3 rounded-sm bg-green-500" />
              <span className="text-muted-foreground">Views</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <div className="w-3 h-3 rounded-sm bg-purple-500" />
              <span className="text-muted-foreground">Comparisons</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <SimpleBarChart data={analytics?.time_series || []} height={200} />
        </CardContent>
      </Card>

      {/* Secondary Stats */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Platform Suggestions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-primary" />
              Platform Suggestions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div>
                <p className="text-2xl font-semibold">{analytics?.suggestions?.total || 0}</p>
                <p className="text-xs text-muted-foreground">Total submitted</p>
              </div>
              {analytics?.suggestions?.pending > 0 && (
                <Badge variant="secondary" className="bg-warning/10 text-warning">
                  {analytics.suggestions.pending} pending review
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              {datePreset === 'all' ? 'Total Events' : 'Events in Period'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <p className="text-2xl font-semibold">{analytics?.recent_events_7d || 0}</p>
              <p className="text-xs text-muted-foreground">
                {datePreset === 'all' ? 'All time' : `In selected period`}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Popular Platforms */}
      {analytics?.popular_platforms?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="w-4 h-4 text-primary" />
              Most Viewed Platforms
              {datePreset !== 'all' && (
                <span className="text-xs font-normal text-muted-foreground">
                  ({currentPreset.label})
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.popular_platforms.slice(0, 5).map((item, index) => {
                const platform = platforms.find(p => p.id === item.platform_id);
                const maxViews = analytics.popular_platforms[0]?.views || 1;
                const percentage = Math.round((item.views / maxViews) * 100);
                
                return (
                  <div key={item.platform_id} className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground w-6">#{index + 1}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">
                          {platform?.name || item.platform_id}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {item.views} views
                        </span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
